import type { BaseDocument } from '../mongo/base-document.interface';
import type { PhoneNumber } from '../phone';
import type { UserScope } from '../permissions/role.interface';
import type { VehicleProfile } from '../vehicle-template';

export const USER_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING: 'pending',
} as const;

export type UserStatus = (typeof USER_STATUS)[keyof typeof USER_STATUS];

export interface DriverAttributes {
  carrierId: string;
  defaultVehicleId?: string | null;
}

export interface User extends BaseDocument {
  canBeReinvited: boolean;
  driverAttributes?: DriverAttributes | null;
  /**
   * Computed at self-resolution time for drivers only: `scopes` intersected with their carrier's
   * enabled perimeter (`Carrier.enabledScopes`). Never stored — recomputed on every `resolveUser()`
   * call and used as `currentUserScopes` only for requests carrying the mobile driver client header.
   */
  driverEffectiveScopes?: UserScope[];
  email: string;
  firstName: string;
  lastActive: Date | null;
  lastName: string;
  phone?: PhoneNumber | null;
  roles: string[];
  scopes: UserScope[];
  status: UserStatus;
}

/**
 * Minimal User reference embedded (denormalized) into other documents (e.g. a task's driver).
 */
export type UserSummary = Pick<User, 'id' | 'firstName' | 'lastName' | 'email' | 'phone'>;

/**
 * Payload for creating a new User.
 * Omits BaseDocument fields that are auto-generated (id, timestamps).
 * tenantId is optional — the repository will assign a default if not provided.
 */
export type CreateUserData = Omit<
  User,
  'createdAt' | 'deletedAt' | 'driverEffectiveScopes' | 'id' | 'lastActive' | 'updatedAt' | 'tenantId'
> & {
  tenantId?: string;
};

/** Subset of User fields that can be updated after creation. */
export type UpdateUserData = Partial<
  Pick<
    User,
    'canBeReinvited' | 'driverAttributes' | 'email' | 'firstName' | 'lastName' | 'phone' | 'roles' | 'scopes' | 'status'
  >
>;

export interface UserScopedPermissions {
  permissions: string[];
  roles: string[];
  scopes: UserScope[];
}

/** A user scope enriched with the resolved entity name. Only used in API responses, never stored. */
export interface ResolvedUserScope extends UserScope {
  /** The human-readable name of the scope entity (space name, site name, or level for tenant/carrier). */
  name: string;
  /** For site-level scopes, the parent space ID. Allows the frontend to build the full Tenant > Space > Site breadcrumb. */
  parentSpaceId?: string;
  /** For site-level scopes, the parent space name. */
  parentSpaceName?: string;
  /** For space-level scopes, the number of sites in this space. */
  siteCount?: number;
}

/** Aggregated scope topology counts for the current user. */
export interface ScopeSummary {
  /** Total number of distinct sites the user has access to. */
  siteCount: number;
  /** Per-space site counts. Keys are space IDs, values are site counts. */
  siteCountsBySpace: Record<string, number>;
  /** Total number of distinct spaces the user has access to. */
  spaceCount: number;
}

/**
 * Resolved vehicle template reference embedded in a driver's default vehicle info.
 * Provides the name and profile icon identifier for frontend rendering.
 */
export interface ResolvedDefaultVehicleTemplate {
  id: string;
  name: string;
  profile: VehicleProfile;
}

/**
 * Default vehicle resolved for display purposes.
 * Computed at response time — never stored in the database.
 */
export interface ResolvedDefaultVehicle {
  id: string;
  licensePlate: string;
  vehicleTemplate: ResolvedDefaultVehicleTemplate;
}

/** User with resolved scope names, returned by search and single-resource endpoints. */
export interface UserWithResolvedScopes extends User {
  resolvedDefaultVehicle?: ResolvedDefaultVehicle | null;
  resolvedScopes: ResolvedUserScope[];
}

/**
 * A driver's carrier resolved for display purposes (self-assign UI).
 * Computed at response time — never stored in the database.
 */
export interface ResolvedUserCarrier {
  id: string;
  name: string;
}

/** Response type for GET /users/me. Extends UserWithResolvedScopes with scope summary counts. */
export interface CurrentUserResponse extends UserWithResolvedScopes {
  /** The driver's carrier, resolved for the self-assign UI. Null/absent when not a driver or the carrier is missing. */
  resolvedCarrier?: ResolvedUserCarrier | null;
  /** Aggregated counts derived from the user's scopes. Eliminates the need for separate count API calls. */
  scopeSummary: ScopeSummary;
}
